import React from "react";
import {
  Edit,
  FilterCenterFocus,
  MonitorWeight,
  OpenInNew,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Divider,
  Grid2,
  Link,
  Paper,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { alpha, lighten, Theme } from "@mui/material/styles";
import { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { iconFillColors } from "../../../../components/shared/icons/Icon.types";
import PalletsIcon from "../../../../components/shared/icons/PalletsIcon";
import PiecesIcon from "../../../../components/shared/icons/PiecesIcon";
import { PickupEntryInfoRow } from "../../../../components/shared/PickupEntryInfoRow";
import Table from "../../../../components/shared/table/Table";
import { DaylightDateFormat } from "../../../../constants/DaylightDateFormat";
import { DASH_PLACEHOLDER } from "../../../../constants/dashPlaceholder";
import { useRateQuoteReview } from "../../../../hooks/react-query/backOffice/clientServices/useRateQuoteReview";
import Loading from "../../../../components/shared/layout/Loading";
import { ErrorDisplay } from "../../../../components/shared/ErrorDisplay";
import { displayMessage } from "../../../../constants/strings";
import { RateQuoteServiceOptionCard } from "./RateQuoteServiceOptionCard";

const reviewHeaderActionButtonBaseSx = {
  height: 42,
  minHeight: 42,
  maxHeight: 42,
  borderRadius: "4px",
  boxSizing: "border-box",
  textTransform: "none" as const,
  px: 2,
  py: 0,
} as const;

const handlingUnitRowSchema = z.object({
  id: z.string(),
  quantity: z.number(),
  handlingUnit: z.string(),
  length: z.number(),
  width: z.number(),
  height: z.number(),
  linearFeet: z.number(),
  density: z.number(),
  pieces: z.number(),
  description: z.string(),
  nmfc: z.string(),
  sub: z.string(),
  class: z.string(),
  rateClass: z.string(),
  weight: z.number(),
});

type HandlingUnitRow = z.infer<typeof handlingUnitRowSchema>;

type ServiceSelectionForm = {
  selectedServiceId: string;
};

const TruncatedTextWithTooltip = ({
  text,
  sx,
}: {
  text: string;
  sx?: React.ComponentProps<typeof Typography>["sx"];
}) => {
  const [showTooltip, setShowTooltip] = React.useState(false);

  const handleMouseEnter = (event: React.MouseEvent<HTMLSpanElement>) => {
    const element = event.currentTarget;
    setShowTooltip(element.scrollWidth > element.clientWidth);
  };

  return (
    <Tooltip title={text} arrow disableHoverListener={!showTooltip}>
      <Typography
        onMouseEnter={handleMouseEnter}
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          ...sx,
        }}
      >
        {text}
      </Typography>
    </Tooltip>
  );
};

const DescriptionCell = ({ text }: { text: string }) => (
  <TruncatedTextWithTooltip
    text={text}
    sx={{
      maxWidth: "200px",
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: "143%",
      letterSpacing: "0.17px",
      color: (theme) => alpha(theme.palette.text.primary, 0.87),
    }}
  />
);

const columns: ColumnDef<HandlingUnitRow>[] = [
  {
    accessorKey: "handlingUnit",
    header: "H. Units",
    cell: ({ row }) => `${row.original.quantity} ${row.original.handlingUnit}`,
    enableSorting: false,
  },
  {
    accessorKey: "length",
    header: "Length",
    enableSorting: false,
  },
  {
    accessorKey: "width",
    header: "Width",
    enableSorting: false,
  },
  {
    accessorKey: "height",
    header: "Height",
    enableSorting: false,
  },
  {
    accessorKey: "linearFeet",
    header: "Linear Feet",
    cell: ({ getValue }) => `${getValue()}ft`,
    enableSorting: false,
  },
  {
    accessorKey: "density",
    header: "Density",
    enableSorting: false,
  },
  {
    accessorKey: "pieces",
    header: "Pieces",
    enableSorting: false,
  },
  {
    accessorKey: "description",
    header: "Description",
    enableSorting: false,
    cell: ({ getValue }) => <DescriptionCell text={String(getValue())} />,
  },
  {
    accessorKey: "nmfc",
    header: "NMFC",
    enableSorting: false,
  },
  {
    accessorKey: "sub",
    header: "Sub",
    enableSorting: false,
  },
  {
    accessorKey: "class",
    header: "Class",
    enableSorting: false,
  },
  {
    accessorKey: "rateClass",
    header: "Rate Class",
    enableSorting: false,
  },
  {
    accessorKey: "weight",
    header: "Total Weight",
    cell: ({ getValue }) => `${getValue()}lbs`,
    enableSorting: false,
  },
];

// Component export is used by React Router lazy loading (BackOffice.tsx route: "rate-quote/review")
// eslint-disable-next-line import/no-unused-modules
export const Component = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useRateQuoteReview();
  const { control } = useForm<ServiceSelectionForm>({
    defaultValues: {
      selectedServiceId: "",
    },
    values: data
      ? { selectedServiceId: data.pricing.standard.quoteId }
      : undefined,
  });

  if (isLoading) {
    return <Loading />;
  }

  if (isError || !data) {
    return (
      <ErrorDisplay
        primaryMessage={displayMessage.default.internalError}
        secondaryMessage="Failed to load rate quote details"
      />
    );
  }

  const { order, pricing, serviceCards, pendingQuoteNumber } = data;

  const quoteDate = order.quoteDate
    ? dayjs(order.quoteDate).format(DaylightDateFormat.DATE)
    : dayjs().format(DaylightDateFormat.DATE);

  // Calculate totals from handling units
  const totalHandlingUnits =
    order.handlingUnits.reduce(
      (sum, unit) => sum + Number(unit.quantity || 0),
      0
    ) || 0;
  const totalPieces =
    order.handlingUnits.reduce(
      (sum, unit) =>
        sum +
        (unit.items.reduce(
          (itemSum, item) => itemSum + Number(item.pieces || 0),
          0
        ) || 0),
      0
    ) || 0;
  const totalWeight =
    order.handlingUnits.reduce(
      (sum, unit) =>
        sum +
        (unit.items.reduce(
          (itemSum, item) => itemSum + Number(item.weight || 0),
          0
        ) || 0),
      0
    ) || 0;
  const averageDensity = order.handlingUnits.length
    ? (
        order.handlingUnits.reduce(
          (sum, unit) => sum + Number(unit.density || 0),
          0
        ) / order.handlingUnits.length
      ).toFixed(1)
    : "0";

  // Transform handling units data for table
  const tableData: HandlingUnitRow[] = order.handlingUnits.map((unit) => {
    const item = unit.items[0];
    return {
      id: unit.id,
      quantity: Number(unit.quantity),
      handlingUnit: unit.handlingUnit,
      length: unit.length,
      width: unit.width,
      height: unit.height,
      linearFeet: Number(unit.linearFeet),
      density: Number(unit.density),
      pieces: Number(item.pieces),
      description: item.description,
      nmfc: item.nmfc,
      sub: item.sub,
      class: String(item.class),
      rateClass: item.rateClass,
      weight: Number(item.weight),
    };
  });

  const handleEditQuote = () => {
    navigate("/back-office/clients/rate-quote");
  };

  const handleCreateQuote = () => {
    // TODO(CS-344): Replace this fallback navigation with the final quote creation flow.
    navigate("/back-office/clients/rate-quote");
  };

  return (
    <Box
      sx={{
        p: 3,
        pb: 8,
        height: "100vh",
        overflow: "auto",
        backgroundColor: "background.default",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 5,
        }}
      >
        <Typography component="h1" sx={{ fontSize: "24px", fontWeight: 700 }}>
          Review Quote
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Edit sx={{ fontSize: 16 }} />}
            onClick={handleEditQuote}
            sx={{
              ...reviewHeaderActionButtonBaseSx,
              minWidth: 98,
              fontWeight: 400,
              color: "primary.main",
              borderColor: (theme) => alpha(theme.palette.primary.main, 0.5),
              "&:hover": {
                borderColor: "primary.dark",
                backgroundColor: (theme) =>
                  lighten(theme.palette.primary.main, 0.965),
              },
            }}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateQuote}
            sx={{
              ...reviewHeaderActionButtonBaseSx,
              minWidth: 142,
              fontWeight: 500,
            }}
          >
            Create Quote
          </Button>
        </Stack>
      </Box>

      <Divider />

      <Card
        sx={{
          mt: 5,
          mb: 3,
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "none",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 0,
          }}
        >
          <Box sx={{ px: 2, borderRight: "1px solid", borderColor: "divider" }}>
            <PickupEntryInfoRow
              label="Quote Date"
              value={quoteDate}
              labelFontSize="20px"
              labelFontWeight={700}
            />
          </Box>
          <Box sx={{ px: 2, borderRight: "1px solid", borderColor: "divider" }}>
            <PickupEntryInfoRow
              label="Pickup on"
              value={
                order.pickupDate
                  ? dayjs(order.pickupDate).format(
                      DaylightDateFormat.DATE_WITH_SHORT_DAY_OF_WEEK
                    ) +
                    " " +
                    order.pickupTimeWindow
                  : DASH_PLACEHOLDER
              }
              labelFontSize="20px"
              labelFontWeight={700}
            />
          </Box>
          <Box sx={{ px: 2, borderRight: "1px solid", borderColor: "divider" }}>
            <PickupEntryInfoRow
              label="Caller details"
              value={order.callerContactName || "-"}
              labelFontSize="20px"
              labelFontWeight={700}
            />
          </Box>
          <Box sx={{ px: 2, borderRight: "1px solid", borderColor: "divider" }}>
            <PickupEntryInfoRow
              label="Account Executive"
              value={order.accountExecutive || order.createdBy || "N/A"}
              labelFontSize="20px"
              labelFontWeight={700}
            />
          </Box>
          <Box sx={{ px: 2 }}>
            <PickupEntryInfoRow
              label="Created by"
              value={order.createdBy || "-"}
              labelFontSize="20px"
              labelFontWeight={700}
            />
          </Box>
        </Box>
      </Card>

      <Paper
        sx={{
          p: 2,
          mb: 3,
          boxShadow: "none",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Grid2 container spacing={1.5}>
          <Grid2 size={{ xs: 2.4 }}>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 400,
                color: "text.primary",
                mb: 1,
              }}
            >
              Shipper
            </Typography>
            <TruncatedTextWithTooltip
              text={order.callerAccountName}
              sx={{
                fontSize: "20px",
                fontWeight: 700,
                mb: 0.5,
                lineHeight: "160%",
                letterSpacing: "0.15px",
              }}
            />
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 700,
                mb: 0.5,
                lineHeight: "160%",
                letterSpacing: "0.15px",
              }}
            >
              ({order.callerAccount})
            </Typography>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 400,
                color: "text.primary",
                mb: 0.5,
                lineHeight: "150%",
                letterSpacing: "0.15px",
              }}
            >
              {order.callerAddress}, {order.callerCity},
            </Typography>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 400,
                color: "text.primary",
                mb: 1,
                lineHeight: "150%",
                letterSpacing: "0.15px",
              }}
            >
              {order.callerState}, {order.callerCountry}, {order.callerZip}
            </Typography>
            <Link
              href="#"
              onClick={(e) => e.preventDefault()}
              underline="hover"
              sx={{
                fontSize: "15px",
                fontWeight: 500,
                lineHeight: "26px",
                letterSpacing: "0.46px",
                color: "primary.main",
              }}
            >
              View Profile
            </Link>
          </Grid2>
          <Grid2 size={{ xs: 2.4 }}>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 400,
                color: "text.primary",
                mb: 1,
              }}
            >
              Consignee
            </Typography>
            <TruncatedTextWithTooltip
              text={order.consigneeAccountName}
              sx={{
                fontSize: "20px",
                fontWeight: 700,
                mb: 0.5,
                lineHeight: "160%",
                letterSpacing: "0.15px",
              }}
            />
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 700,
                mb: 0.5,
                lineHeight: "160%",
                letterSpacing: "0.15px",
              }}
            >
              ({order.consigneeAccount})
            </Typography>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 400,
                color: "text.primary",
                mb: 0.5,
                lineHeight: "150%",
                letterSpacing: "0.15px",
              }}
            >
              {order.consigneeAddress}, {order.consigneeCity},
            </Typography>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 400,
                color: "text.primary",
                mb: 1,
                lineHeight: "150%",
                letterSpacing: "0.15px",
              }}
            >
              {order.consigneeState}, {order.consigneeCountry},{" "}
              {order.consigneeZip}
            </Typography>
            <Link
              href="#"
              onClick={(e) => e.preventDefault()}
              underline="hover"
              sx={{
                fontSize: "15px",
                fontWeight: 500,
                lineHeight: "26px",
                letterSpacing: "0.46px",
                color: "primary.main",
              }}
            >
              View Profile
            </Link>
          </Grid2>
          <Grid2 size={{ xs: 2.4 }}>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 400,
                color: "text.primary",
                mb: 1,
              }}
            >
              Bill to
            </Typography>
            <TruncatedTextWithTooltip
              text={order.billToAccountName}
              sx={{
                fontSize: "20px",
                fontWeight: 700,
                mb: 0.5,
                lineHeight: "160%",
                letterSpacing: "0.15px",
              }}
            />
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 700,
                mb: 0.5,
                lineHeight: "160%",
                letterSpacing: "0.15px",
              }}
            >
              ({order.billToAccount})
            </Typography>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 400,
                color: "text.primary",
                mb: 0.5,
                lineHeight: "150%",
                letterSpacing: "0.15px",
              }}
            >
              {order.billToAddress}, {order.billToCity},
            </Typography>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 400,
                color: "text.primary",
                mb: 1,
                lineHeight: "150%",
                letterSpacing: "0.15px",
              }}
            >
              {order.billToState}, {order.billToCountry}, {order.billToZip}
            </Typography>
            <Link
              href="#"
              onClick={(e) => e.preventDefault()}
              underline="hover"
              sx={{
                fontSize: "15px",
                fontWeight: 500,
                lineHeight: "26px",
                letterSpacing: "0.46px",
                color: "primary.main",
              }}
            >
              View Profile
            </Link>
          </Grid2>
          <Grid2 size={{ xs: 2.4 }}>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 400,
                color: "text.primary",
                mb: 1,
              }}
            >
              Service Level
            </Typography>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 700,
                lineHeight: "160%",
                letterSpacing: "0.15px",
              }}
            >
              {order.serviceLevel}
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 2.4 }}>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 400,
                color: "text.primary",
                mb: 1,
              }}
            >
              Transit Time
            </Typography>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 700,
                mb: 0.5,
                lineHeight: "160%",
                letterSpacing: "0.15px",
              }}
            >
              {order.transitTime}
            </Typography>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                color: (theme) => alpha(theme.palette.text.primary, 0.6),
                mb: 0.5,
                lineHeight: "160%",
                letterSpacing: "0.4px",
              }}
            >
              Est. Delivery:{" "}
              {order.estimatedDeliveryDate
                ? dayjs(order.estimatedDeliveryDate).format(
                    DaylightDateFormat.SHORT_DATE_WITH_WEEKDAY_PADDED
                  )
                : DASH_PLACEHOLDER}
            </Typography>
            <Link
              href="#"
              onClick={(e) => e.preventDefault()}
              underline="hover"
              sx={{
                fontSize: "15px",
                fontWeight: 500,
                lineHeight: "26px",
                letterSpacing: "0.46px",
                color: "primary.main",
              }}
            >
              View more options
            </Link>
          </Grid2>
        </Grid2>
      </Paper>

      <Paper
        sx={{
          p: 2,
          mb: 3,
          boxShadow: "none",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Table
          data={tableData}
          columns={columns}
          caption="Handling Units Details"
          rowSelectionMode="unselectable"
          styles={{
            "& .MuiTable-root": {
              border: "1px solid",
              borderColor: "divider",
              tableLayout: "fixed",
              width: "100%",
            },
            "& .MuiTableHead-root .MuiTableCell-root": {
              backgroundColor: "transparent",
              fontFamily: (theme) => (theme as Theme).typography.fontFamily,
              fontWeight: 700,
              fontSize: "12px",
              lineHeight: "168%",
              letterSpacing: "0.4px",
              color: (theme) =>
                alpha((theme as Theme).palette.text.primary, 0.87),
              width: "calc(100% / 12)",
              "&:first-of-type": {
                paddingLeft: 3,
              },
            },
            "& .MuiTableBody-root .MuiTableCell-root": {
              fontFamily: (theme) => (theme as Theme).typography.fontFamily,
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "143%",
              letterSpacing: "0.17px",
              color: (theme) =>
                alpha((theme as Theme).palette.text.primary, 0.87),
              py: 1,
              width: "calc(100% / 12)",
              "&:first-of-type": {
                paddingLeft: 3,
              },
            },
            "& .MuiTableRow-root": {
              cursor: "default",
              "&:hover": {
                backgroundColor: "transparent",
              },
            },
          }}
        />

        <Card
          sx={{
            mt: 2,
            p: 0,
            border: "none",
            boxShadow: "none",
            backgroundColor: (theme: Theme) =>
              alpha(theme.palette.primary.main, 0.04),
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <PalletsIcon
                  fill={iconFillColors.gray}
                  sx={{ fontSize: "20px" }}
                />
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 700,
                    color: "primary.dark",
                    lineHeight: 1.43,
                    letterSpacing: "0.17px",
                  }}
                >
                  Total Handling Units
                </Typography>
              </Stack>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  color: "primary.dark",
                  lineHeight: 1.43,
                  letterSpacing: "0.17px",
                  mt: 0.5,
                  ml: "28px",
                }}
              >
                {totalHandlingUnits}
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <PiecesIcon
                  fill={iconFillColors.gray}
                  sx={{ fontSize: "20px" }}
                />
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 700,
                    color: "primary.dark",
                    lineHeight: 1.43,
                    letterSpacing: "0.17px",
                  }}
                >
                  Total Pieces
                </Typography>
              </Stack>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  color: "primary.dark",
                  lineHeight: 1.43,
                  letterSpacing: "0.17px",
                  mt: 0.5,
                  ml: "28px",
                }}
              >
                {totalPieces}
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <MonitorWeight
                  fontSize="small"
                  sx={{ color: "action.active" }}
                />
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 700,
                    color: "primary.dark",
                    lineHeight: 1.43,
                    letterSpacing: "0.17px",
                  }}
                >
                  Total Weight
                </Typography>
              </Stack>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  color: "primary.dark",
                  lineHeight: 1.43,
                  letterSpacing: "0.17px",
                  mt: 0.5,
                  ml: "28px",
                }}
              >
                {totalWeight}
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FilterCenterFocus
                  fontSize="small"
                  sx={{ color: "action.active" }}
                />
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 700,
                    color: "primary.dark",
                    lineHeight: 1.43,
                    letterSpacing: "0.17px",
                  }}
                >
                  Density
                </Typography>
              </Stack>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  color: "primary.dark",
                  lineHeight: 1.43,
                  letterSpacing: "0.17px",
                  mt: 0.5,
                  ml: "28px",
                }}
              >
                {averageDensity}
              </Typography>
            </Box>
          </Box>
        </Card>

        <Divider sx={{ my: 3 }} />

        <Grid2 container spacing={3}>
          <Grid2 size={{ xs: 4 }}>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ fontSize: "16px", mb: 1 }}
            >
              Pickup Accessorial
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 2,
                m: 0,
                "& li": { fontSize: "16px", fontWeight: 400, mb: 0.5 },
              }}
            >
              {order.additionalServices.pickup.map((service) => (
                <li key={service}>{service}</li>
              ))}
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 4 }}>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ fontSize: "16px", mb: 1 }}
            >
              Delivery Accessorial
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 2,
                m: 0,
                "& li": { fontSize: "16px", fontWeight: 400, mb: 0.5 },
              }}
            >
              {order.additionalServices.delivery.map((service) => (
                <li key={service}>{service}</li>
              ))}
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 4 }}>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ fontSize: "16px", mb: 1 }}
            >
              Both Accessorial
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 2,
                m: 0,
                "& li": { fontSize: "16px", fontWeight: 400, mb: 0.5 },
              }}
            >
              {order.additionalServices.accessorial.map((service) => (
                <li key={service}>{service}</li>
              ))}
            </Box>
          </Grid2>
        </Grid2>

        <Grid2 container spacing={3} sx={{ mt: 3 }}>
          <Grid2 size={{ xs: 4 }}>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ fontSize: "16px", mb: 1 }}
            >
              Daylight Protect Coverage
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: "16px", fontWeight: 400 }}
            >
              {order.daylightProtect.declareAmount}
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 4 }}>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ fontSize: "16px", mb: 1 }}
            >
              Trace Numbers
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: "16px", fontWeight: 400 }}
            >
              {order.additionalInfo.additionalFields[0]?.traceNumber}
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 4 }}>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ fontSize: "16px", mb: 1 }}
            >
              Uploads
            </Typography>
            <Stack spacing={0.5}>
              {order.additionalInfo.uploads.map((upload) => (
                <Link
                  key={upload.id}
                  href={upload.fileUrl}
                  underline="hover"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 700,
                    lineHeight: "150%",
                    letterSpacing: "0.15px",
                    color: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  {upload.fileName}
                  <OpenInNew sx={{ fontSize: "18px" }} />
                </Link>
              ))}
            </Stack>
          </Grid2>
        </Grid2>
      </Paper>

      <Box sx={{ mt: 1 }}>
        <Divider sx={{ mb: 2 }} />
        <Typography
          component="h2"
          sx={{
            fontSize: "20px",
            fontWeight: 500,
            color: "text.primary",
            mb: 2,
          }}
        >
          Select Service for your Order:
        </Typography>
        <Controller
          name="selectedServiceId"
          control={control}
          render={({ field }) => (
            <RadioGroup value={field.value} onChange={field.onChange}>
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  flexDirection: "row",
                  alignItems: "flex-start",
                  width: "100%",
                }}
              >
                {serviceCards.map((card) => (
                  <RateQuoteServiceOptionCard
                    key={card.key}
                    serviceType={
                      card.key === "ltl"
                        ? "standard"
                        : card.key === "uc"
                          ? "guaranteed"
                          : "volume"
                    }
                    quoteDetails={pricing}
                    serviceDisplayName={card.serviceDisplayName}
                    quoteNumberDisplay={pendingQuoteNumber}
                    quoteNumberPrefix=""
                    showTruckIcon={card.showTruckIcon}
                    showRecommendedBadge={card.showRecommendedBadge}
                    showSpeedIcon={card.showSpeedIcon}
                    includeGuaranteedServiceLine={
                      card.includeGuaranteedServiceLine
                    }
                    collapsedPriceAddon={card.collapsedPriceAddon}
                    guaranteedServiceChargeDisplay={
                      card.guaranteedServiceChargeDisplay
                    }
                    isSelected={field.value === card.quoteId}
                    onSelect={(serviceId) => field.onChange(serviceId)}
                  />
                ))}
              </Box>
            </RadioGroup>
          )}
        />
      </Box>
    </Box>
  );
};
